import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, Checkbox, CircularProgress, Container, createStyles, Dialog, DialogActions, DialogContent, DialogContentText, FormControlLabel, Link, makeStyles, TextField, Theme, Typography } from "@material-ui/core";
import { MyFooter } from "../footer/MyFooter";
import { MyAppBar } from "../appbar/MyAppBar";
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { registerEmailAct } from '../../app/actions';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    container: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    couponImageDiv: {
      width: '75vw',
      height: 0,
      paddingTop: '100%',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
    bottomBox: {
      height: '10vh',
    },
    helperText: {
      color: 'red',
    },
  });
});

type FormType = {
  email: string,
}

export function RegisterEmail(props: {
}): JSX.Element {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const registerEmailStatus = useAppSelector((state) => {
    return state.inputAnswer.couponState!.registerEmailStatus;
  });

  const formSchema: yup.SchemaOf<FormType> = yup.object().shape({
    email: yup
      .string()
      .email(t("email.please_enter_your_email_address_correctly"))
      .required(t("email.please_enter_your_email_address")),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, },
  } = useForm<FormType>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: '',
    }
  });

  const canClickRegister = registerEmailStatus === 'idle' || registerEmailStatus === 'rejected';

  const onSubmit = (formData: FormType) => {

    if (!agreeChecked) {
      setOpen(true);
      return;
    }

    dispatch(registerEmailAct({
      email: formData.email,
    }));

    return false;
  }

  return (
    <Container
      maxWidth={false}
      disableGutters={true}
      className={classes.container}
    >
      <MyAppBar />
      <Box position="relative" flexGrow="1">
        {/* 冒頭メッセージ begin */}
        <Box mt={4} mx={3}>
          <Typography variant="h5" align="left">
            <Box fontWeight="fontWeightBold">
              {/* モンテローザ抽選対応 begin */}
              {`${t(
                "email.please_register_the_delivery_address_information_of_the_cash_voucher"
              )}${t(
                "email.after_the_lottery_we_will_send_the_coupon_to_your_registered_address"
              )}`}
              {/* モンテローザ抽選対応 end */}
              {/* メールアドレスを登録してください。ご登録されたメール宛にクーポンをお送りさせて頂きます。 */}
            </Box>
          </Typography>
        </Box>
        {/* 冒頭メッセージ end */}

        {/* 個人情報同意 begin */}
        <Box mt={4} mx={6}>
          <Typography variant="subtitle1">
            <Link
              target="_blank"
              href="https://corp.clipline.com/cxsurvey-agreement2/"
              underline="always"
            >
              {t('email.notice_and_consent_statement_regarding_our_handling_of_personal_information')}
            </Link>
            {t('email.please_agree_to_and_proceed_to_registration')}
          </Typography>
        </Box>

        <Box mt={3} mx={6}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={agreeChecked}
                onChange={(event) => {
                  setAgreeChecked(!agreeChecked);
                }}
              />
            }
            label={t('common.agree')}
          />
        </Box>
        {/* 個人情報同意 end */}

        {/* チャレンジButton begin */}
        <Box
          position="relative"
          display="flex"
          justifyContent="center"
          mt={4}
          mx={3}
        >
          <form
            style={{
              width: "100%",
            }}
            onSubmit={handleSubmit(onSubmit)}
            method="post"
          >
            <Box>
              <Controller
                name="email"
                control={control}
                render={({ field }) => {
                  return (
                    <TextField
                      {...field}
                      label={t('email.email_address')}
                      placeholder=""
                      color="primary"
                      variant="outlined"
                      fullWidth
                      helperText={errors.email?.message}
                      FormHelperTextProps={{
                        className: classes.helperText,
                      }}
                    />
                  );
                }}
              />
            </Box>

            {/* モンテローザ抽選対応 begin */}
            <Box mt={2}>
              <TextField
                label={t('email.full_name')}
                placeholder=""
                color="primary"
                variant="outlined"
                fullWidth
              />
            </Box>
            <Box mt={2}>
              <TextField
                label={t('email.address')}
                placeholder=""
                color="primary"
                variant="outlined"
                fullWidth
              />
            </Box>
            {/* モンテローザ抽選対応 end */}

            <Box
              display="flex"
              position="relative"
              justifyContent="center"
              alignItems="center"
              mt={3}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={!canClickRegister}
              >
                {t("question.btn.register")}
              </Button>
              {!canClickRegister && (
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  style={{
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </form>
        </Box>
        {/* チャレンジButton end */}

        {/* 最下部余白 begin */}
        <Box className={classes.bottomBox}>&nbsp;</Box>
        {/* 最下部余白 end */}
      </Box>
      <MyFooter />

      {/* 同意にチェックを促すダイアログ begin */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("email.please_check_i_agree")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            variant="contained"
            autoFocus
          >
            {t("question.btn.ok")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 同意にチェックを促すダイアログ end */}
    </Container>
  );
}
